// frontend/src/components/especialistas/AsignarUsuariosInternos.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import usuarioEspecialistaService from '../../services/usuarioEspecialistaService';

const AsignarUsuariosInternos = ({ especialistaId, nombreEspecialista }) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [loadingAsignados, setLoadingAsignados] = useState(false);
  const [usuariosInternos, setUsuariosInternos] = useState([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState([]);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState('');

  // Cargar usuarios asignados y disponibles
  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Obtener usuarios internos asignados a este especialista
      const asignadosResponse = await usuarioEspecialistaService.obtenerUsuariosPorEspecialista(especialistaId);
      setUsuariosInternos(asignadosResponse.data);
      
      // Obtener usuarios internos disponibles para asignar
      const disponiblesResponse = await usuarioEspecialistaService.obtenerUsuariosInternosDisponibles(especialistaId);
      setUsuariosDisponibles(disponiblesResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Error al cargar los usuarios', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [especialistaId]);

  // Asignar usuario interno al especialista
  const handleAsignarUsuario = async () => {
    if (!selectedUsuarioId) return;
    
    setLoadingAsignados(true);
    try {
      await usuarioEspecialistaService.asignarUsuarioAEspecialista(selectedUsuarioId, especialistaId);
      showNotification('Usuario asignado correctamente', 'success');
      setSelectedUsuarioId('');
      await cargarDatos();
    } catch (error) {
      console.error('Error assigning user:', error);
      showNotification('Error al asignar el usuario', 'error');
    } finally {
      setLoadingAsignados(false);
    }
  };

  // Eliminar asignación de usuario
  const handleEliminarAsignacion = async (usuarioId) => {
    setLoadingAsignados(true);
    try {
      await usuarioEspecialistaService.eliminarAsignacion(usuarioId, especialistaId);
      showNotification('Asignación eliminada correctamente', 'success');
      await cargarDatos();
    } catch (error) {
      console.error('Error removing assignment:', error);
      showNotification('Error al eliminar la asignación', 'error');
    } finally {
      setLoadingAsignados(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Asignar Usuarios Internos"
        subheader={`Asigna usuarios internos al especialista: ${nombreEspecialista}`}
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Formulario para asignar nuevo usuario */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" mb={2}>
                <FormControl fullWidth sx={{ mr: 2 }}>
                  <InputLabel id="usuario-select-label">Seleccionar Usuario Interno</InputLabel>
                  <Select
                    labelId="usuario-select-label"
                    value={selectedUsuarioId}
                    onChange={(e) => setSelectedUsuarioId(e.target.value)}
                    label="Seleccionar Usuario Interno"
                    disabled={usuariosDisponibles.length === 0}
                  >
                    {usuariosDisponibles.map((usuario) => (
                      <MenuItem key={usuario.id} value={usuario.id}>
                        {usuario.nombre} {usuario.apellidos}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAsignarUsuario}
                  disabled={!selectedUsuarioId || loadingAsignados}
                >
                  Asignar
                </Button>
              </Box>
            </Grid>

            {/* Lista de usuarios asignados */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Usuarios Internos Asignados
              </Typography>
              {loadingAsignados ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : usuariosInternos.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No hay usuarios internos asignados a este especialista.
                </Typography>
              ) : (
                <List>
                  {usuariosInternos.map((usuario) => (
                    <ListItem key={usuario.id} divider>
                      <ListItemText
                        primary={`${usuario.nombre} ${usuario.apellidos}`}
                        secondary={usuario.email}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleEliminarAsignacion(usuario.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default AsignarUsuariosInternos;